import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IRelabeledVariablesClient, EditVariableLabelCommand, RelabeledVariablesClient } from "app/ApiClient";
import { LoadingState } from "types";
import { initialRelabeledVariablesState } from "./relabeledVariablesState";
import { getTextVariableStatByProjectId } from "slices/textVariablesProject/textVariablesSlice";

const relabeledVariablesClient: IRelabeledVariablesClient = new RelabeledVariablesClient(process.env.REACT_APP_API_URL);

export const editVariableLabel = createAsyncThunk(
  "relabeledVariables/editLabel",
  async (command: EditVariableLabelCommand, { dispatch, rejectWithValue }) => {
    try {
      const result = await relabeledVariablesClient.editLabel(command);
      if (result)
        await dispatch(
          getTextVariableStatByProjectId({
            projectId: command.projectId ?? 0,
            versionId: command.encodedVersionId ?? "",
          }),
        );
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const relabeledVariablesSlice = createSlice({
  name: "relabeledVariables",
  initialState: initialRelabeledVariablesState,
  reducers: {
    clearRelabeledVariableSuccessMessages: (
      state,
      action: { payload: { variableFullName: string; projectId: number } },
    ) => {
      const { projectId, variableFullName } = action.payload;
      state.relabeledVariableSuccessMessages = state.relabeledVariableSuccessMessages.filter(
        (message) => !(message.projectId === projectId && message.variableFullName === variableFullName),
      );
    },

    clearRelabeledVariableErrorMessages: (state, action: { payload: { errorMessage: string; projectId: number } }) => {
      const { projectId, errorMessage } = action.payload;
      state.relabeledVariableErrorMessages = state.relabeledVariableErrorMessages.filter(
        (message) => !(message.projectId === projectId && message.errorMessage === errorMessage),
      );
    },
  },
  extraReducers: (builder) => {
    // editVariableLabel
    builder.addCase(editVariableLabel.pending, (state, { meta }) => {
      const variableFullName = meta.arg.variableFullName;
      if (variableFullName) {
        state.relabeledVariableState[variableFullName] = LoadingState.pending;
      }
    });
    builder.addCase(editVariableLabel.fulfilled, (state, { payload, meta }) => {
      const { arg } = { ...meta };
      const variableFullName = arg.variableFullName;
      if (payload.error || (payload.message && !payload.message.toLowerCase().includes("successfully"))) {
        if (variableFullName) {
          state.relabeledVariableState[variableFullName] = LoadingState.failed;
        }
        const errorMessage = payload.error ?? payload.message;
        errorMessage &&
          state.relabeledVariableErrorMessages.push({
            projectId: arg.projectId ?? 0,
            errorMessage: errorMessage,
          });
      } else {
        if (variableFullName) {
          state.relabeledVariableState[variableFullName] = LoadingState.succeed;
        }
        state.relabeledVariableSuccessMessages.push({
          projectId: arg.projectId ?? 0,
          variableFullName: arg.variableFullName,
        });
      }
    });
    builder.addCase(editVariableLabel.rejected, (state, { error, meta }) => {
      const { arg } = { ...meta };
      const variableFullName = arg.variableFullName;
      if (variableFullName) {
        state.relabeledVariableState[variableFullName] = LoadingState.failed;
      }
      if (typeof error.message === "string") {
        error.message &&
          state.relabeledVariableErrorMessages.push({
            projectId: arg.projectId ?? 0,
            errorMessage: error.message,
          });
      }
    });
  },
});
export const { clearRelabeledVariableSuccessMessages, clearRelabeledVariableErrorMessages } =
  relabeledVariablesSlice.actions;
export default relabeledVariablesSlice.reducer;
